import { FC, ReactNode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { Button } from '@microservices/wiskey-react-components'
import { Close } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  Divider,
  Fade,
  Grid,
  Modal,
  Skeleton,
  SxProps,
  Typography,
} from '@mui/material'

import { ErrorFallback } from '@components/ErrorFallback'

const containerStyle: SxProps = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  minWidth: 540,
  boxShadow: 24,
  p: 4,
  pt: 2,
  pb: 2,
  borderRadius: 1,
}

const closeIconStyle: SxProps = {
  cursor: 'pointer',
}

type ModalWrapperProps = {
  children?: ReactNode
  title?: ReactNode | string
  isShow: boolean
  onClose: (value: boolean) => void
  onSave?: () => void
  btnText?: string
  cancelBtnText?: string
  disabledSave?: boolean
  hideDivider?: boolean
  loading?: boolean
  maxWidth?: number
  width?: number | string
  hideActions?: boolean
  isStickyHeader?: boolean
  fullViewportHeight?: boolean
  isDialogOverModal?: boolean
  height?: number | string
  disableEnforceFocus?: boolean
}

export const ModalWrapper: FC<ModalWrapperProps> = ({
  children,
  title,
  isShow,
  onClose,
  onSave,
  btnText,
  cancelBtnText,
  disabledSave,
  hideDivider,
  loading,
  maxWidth,
  width,
  hideActions,
  isStickyHeader,
  fullViewportHeight,
  isDialogOverModal,
  height,
  disableEnforceFocus,
}) => {
  const { t } = useTranslation()

  const handleSave = () => {
    if (onSave) {
      onSave()
    }
  }

  const handleClose = (value: boolean) => {
    if (onClose) {
      onClose(value)
    }
  }

  return (
    <Modal
      aria-describedby='transition-modal-description'
      aria-labelledby='transition-modal-title'
      disableEnforceFocus={disableEnforceFocus}
      open={isShow}
      sx={{
        zIndex: theme => (isDialogOverModal ? theme.zIndex.modal - 3 : theme.zIndex.modal),
      }}
    >
      <Fade
        in={isShow}
        style={
          fullViewportHeight
            ? { height: 'calc(100vh - 4px)', overflow: 'hidden' }
            : {
                height,
              }
        }
      >
        <Box sx={{ ...containerStyle, maxWidth, width }}>
          <Grid
            container
            alignItems='center'
            justifyContent='space-between'
            position={isStickyHeader ? 'sticky' : undefined}
            sx={{ mb: 1 }}
          >
            <Typography flexGrow={1} variant='body1'>
              {loading ? <Skeleton /> : title}
            </Typography>
            <Close sx={closeIconStyle} onClick={() => handleClose(false)} />
          </Grid>
          {!hideDivider && <Divider />}
          {loading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <ErrorBoundary FallbackComponent={ErrorFallback}>{children}</ErrorBoundary>
          )}
          {!hideDivider && <Divider />}
          {!hideActions && (
            <Grid container alignItems='center' gap={1} justifyContent='flex-end' sx={{ mt: 1 }}>
              <Button onClick={() => onClose(false)}>
                {cancelBtnText ?? t('modal.closeBtnText')}
              </Button>
              <Button disabled={disabledSave} variant='contained' onClick={handleSave}>
                {btnText ?? t('modal.saveBtnText')}
              </Button>
            </Grid>
          )}
        </Box>
      </Fade>
    </Modal>
  )
}
